import {lightenColor} from 'components/customer/Confirmation/helpers/lightenColor';
import {useLazyGetQuickProductsQuery} from 'components/customer/Product/store/productApi';
import {
    selectSearchText,
    setSearchText,
} from 'components/customer/Product/store/productSlice';
import {getErrorMessage} from 'shared/helpers/getErrorMessage';
import {useJobContext, useProductContext} from 'contexts';
import Product from 'IndexedDB/models/Product';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useSearchParams} from 'react-router-dom';
import {CBCButton, Icon, productFilter} from 'shared/helpers';
import {useAppDispatch, useAppSelector} from 'store/customer';
import styled from 'styled-components';
import {Items} from 'shared/components/Product/Items';
import {ItemType} from 'shared/components/Product/Item';
import {CategoryContainer} from 'shared/components/Product/Categories';
import {searchItems} from 'service/HardwareService';
import {mobileLandscape, mobilePortrait} from 'shared/Breakpoints';
import {FAVOURITE_CATEGORY_NAME} from 'hooks/QuickProduct/ProductSearch';
import {PartialJob} from 'shared/types/PartialJob';
import {getProductParams} from 'hooks/preCacheProducts';
import {skipToken} from '@reduxjs/toolkit/query';

export interface NoProductsInterface {
    isProduct?: boolean;
    setError?: (errors: string[]) => void;
}

export const NoProducts = ({isProduct, setError}: NoProductsInterface) => {
    const {room} = useJobContext() as PartialJob;
    const {breadcrumbs, isMiniBrowser} = useProductContext();
    const dispatch = useAppDispatch();
    const [, setSearchParams] = useSearchParams();
    const text = useAppSelector(selectSearchText);

    const showAllProductsClickHandler = useCallback(() => {
        dispatch(setSearchText(undefined));
        setSearchParams({products: 'all'});
    }, []);

    const clearSearchHandler = useCallback(() => {
        dispatch(setSearchText(undefined));
    }, []);

    const [trigger] = useLazyGetQuickProductsQuery();
    const [loading, setLoading] = useState(true);
    const [hasExtra, setHasExtra] = useState(false);
    const [items, setItems] = useState<ItemType[]>([]);
    const [category, setCategory] = useState('');

    const extraSearchItems = useCallback(async () => {
        setLoading(true);
        setHasExtra(false);
        setItems([]);
        setCategory('');
        if (text) {
            if (breadcrumbs && breadcrumbs.length) {
                const item = breadcrumbs[breadcrumbs.length - 1];
                setCategory(item.key);
            }

            try {
                if (isProduct) {
                    const params = getProductParams({room});
                    if (params === skipToken) return;

                    const {
                        data: productList,
                        isError,
                        error,
                    } = await trigger(params, true);

                    if (isError) {
                        throw new Error(getErrorMessage(error));
                    }

                    if (productList) {
                        const items = productFilter(
                            productList,
                            false,
                            [],
                            [],
                            text
                        ) as Product[];

                        if (items.length > 0) {
                            setHasExtra(true);
                            setItems(
                                items
                                    .map((item) => ({
                                        ...item,
                                        name: item.text,
                                        imageUrl: item.image,
                                        sortingName:
                                            item.changed_name || item.text,
                                    }))
                                    .sort((productA, productB) =>
                                        productA.sortingName.localeCompare(
                                            productB.sortingName
                                        )
                                    ) as unknown as ItemType[]
                            );
                        }
                    }
                } else {
                    const items = await searchItems(undefined, text);

                    if (
                        items &&
                        items.sundry_items &&
                        Array.isArray(items.sundry_items) &&
                        items.sundry_items.length > 0
                    ) {
                        setHasExtra(true);
                        setItems(items.sundry_items as unknown as ItemType[]);
                    }
                }
            } catch (e) {
                // fail quitely without any action, display generic message
                // eslint-disable-next-line no-console -- check message above
                console.log(e);
            } finally {
                setLoading(false);
            }
        }
    }, [breadcrumbs, text, isProduct, room]);

    useEffect(() => {
        void extraSearchItems();
    }, [extraSearchItems]);

    const details = useMemo(() => {
        if (hasExtra) {
            return (
                <>
                    No products found{category ? ` in ${category}` : ''}{' '}
                    {category == FAVOURITE_CATEGORY_NAME ? '' : 'category'} for{' '}
                    <em>&ldquo;{text}&rdquo;</em> — check the potential matches
                    below.
                </>
            );
        } else {
            return (
                <>
                    No products found <em>&ldquo;{text}&rdquo;</em>
                </>
            );
        }
    }, [category, hasExtra, text]);

    if (loading) {
        return null;
    }

    return (
        <>
            <Container
                $hasExtraResult={hasExtra}
                $isMiniBrowser={isMiniBrowser ?? false}>
                <CircleIcon>
                    <Icon iconName="SearchOff.svg" />
                </CircleIcon>
                <MessageContainer>
                    <Strong>{details}</Strong>
                    <Message>
                        Try
                        <ul>
                            <li>Checking for typos or spelling</li>
                            <li>Using fewer or more general keywords</li>
                            <li>Searching by category instead</li>
                        </ul>
                    </Message>
                </MessageContainer>

                <ButtonContainer>
                    <SecondaryButton onClick={clearSearchHandler}>
                        Clear search
                    </SecondaryButton>
                    <ContrastCBCButton onClick={showAllProductsClickHandler}>
                        Show all products
                    </ContrastCBCButton>
                </ButtonContainer>
            </Container>

            {items.length ? (
                <ItemsContainer>
                    <h2>Products that might interest you</h2>
                    <Items
                        items={items}
                        noHr={true}
                        isProduct={isProduct}
                        setError={setError}
                        loader={false}
                        showScrollButton={false}
                    />
                </ItemsContainer>
            ) : null}
        </>
    );
};

const Message = styled.div`
    color: #858585;
    font-size: 0.9em;
`;

const MessageContainer = styled.div`
    flex: 1;
    text-align: left;
    color: rgb(131 131 131);
`;

const Strong = styled.div`
    font-weight: 400;
    color: ${({theme}) => theme.colors.primary?.main};
    font-size: 1em;

    > em {
        font-weight: 500;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    gap: 15px;
    justify-content: center;
    margin: 0;

    button {
        font-size: 0.9em;
        font-weight: 500;
    }

    ${mobileLandscape`
        flex-direction: column;    
    `}
`;

const CircleIcon = styled.div`
    background: ${({theme}) => theme.colors.primary?.main};
    border-radius: 100px;
    margin-right: 15px;
    width: 70px;
    padding: 12px;
`;

const Container = styled.div<{
    $hasExtraResult: boolean;
    $isMiniBrowser: boolean;
}>`
    display: flex;
    background: ${({theme}) => lightenColor(theme.colors.secondary?.main, 75)};
    padding: 15px;
    border-radius: 8px;
    justify-content: space-between;
    align-items: center;

    ${({$hasExtraResult, $isMiniBrowser}) => {
        if ($hasExtraResult) {
            return `
                ${ButtonContainer} {
                    margin-top: ${$isMiniBrowser ? '15px' : '0px'};
                }

                ${Message} {
                    display: none;
                }
            `;
        }
    }}

    ${({$isMiniBrowser}) => {
        if ($isMiniBrowser) {
            return `
                flex-wrap: wrap;
            `;
        }
    }}

    ${mobilePortrait<{$hasExtraResult: boolean}>`
        flex-wrap: wrap;

        ${ButtonContainer} {
            margin-top: ${({$hasExtraResult}) => ($hasExtraResult ? '15px' : '0')};
        }
    `}
`;

const ItemsContainer = styled.div`
    > h2 {
        color: ${({theme}) => theme.colors.primary?.main};
        background: white;
        text-align: left;
        margin-top: 15px;
    }

    ${CategoryContainer} {
        margin-top: 0;
        margin-bottom: 30px !important;
    }
`;

const SecondaryButton = styled(CBCButton)`
    color: white;
    background: ${({theme}) => theme.colors.secondary?.main};
    border-radius: 30px;
    font-weight: bold;
    text-decoration: none;

    &:hover,
    &:active,
    &:focus {
        background: ${({theme}) => theme.colors.primary?.main};
        color: white;
    }
`;

const ContrastCBCButton = styled(CBCButton)`
    background: white;
    border: 1px solid ${({theme}) => theme.colors.secondary?.main};
    color: ${({theme}) => theme.colors.secondary?.main};
    border-radius: 30px;
    font-weight: bold;
    text-decoration: none;

    &:hover,
    &:active,
    &:focus {
        border-color: ${({theme}) => theme.colors.primary?.main};
        color: ${({theme}) => theme.colors.primary?.main};
    }
`;
